import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { AnalyticsRow } from '../../../types/api/response/analyticsResponse';
import { formatMinutes } from '../../../components/Analytics/format';

// Extended here as well as in the page. Idempotent, and it means this module cannot depend on
// which of the two a bundler happens to evaluate first.
dayjs.extend(utc);
dayjs.extend(timezone);

const FIJI = 'Pacific/Fiji';

/**
 * Which Locus tenant the pull came from, for the feed caption.
 *
 * 🚨 NOT HARDCODED, and the tenancy is why. `superstore-devo` is PRODUCTION and `superstore` is
 * UAT, so a fixed string would eventually caption UAT data as production. Unset omits the name
 * rather than guessing: set REACT_APP_LOCUS_TENANT per environment to show it.
 */
const TENANT = process.env.REACT_APP_LOCUS_TENANT ?? '';

const str = (row: AnalyticsRow, key: string): string =>
  row[key] === null || row[key] === undefined ? '' : String(row[key]);

const num = (row: AnalyticsRow, key: string): number | null =>
  row[key] === null || row[key] === undefined ? null : Number(row[key]);

/**
 * A date range that follows the day until somebody picks a date.
 *
 * 🚨 A WALL DISPLAY IS NEVER RELOADED, so a range built once at mount still showed its opening
 * day a week later, beside a picker that agreed with itself.
 *
 * ⚠️ IT STOPS FOLLOWING THE MOMENT A DATE IS CHOSEN; the only way back is a reload.
 *
 * ⚠️ Polled rather than scheduled for midnight — a machine that sleeps misses a timer.
 */
export const useRollingRange = (build: () => { from: Dayjs; to: Dayjs }, dayKey: () => string) => {
  const builder = useRef(build);
  const key = useRef(dayKey);
  builder.current = build;
  key.current = dayKey;

  const [range, setRangeState] = useState(builder.current);
  const builtFor = useRef(key.current());
  const pinned = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (pinned.current) return;
      const now = key.current();
      if (now === builtFor.current) return;
      builtFor.current = now;
      setRangeState(builder.current());
    }, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const setRange = useCallback((next: React.SetStateAction<{ from: Dayjs; to: Dayjs }>) => {
    pinned.current = true;
    setRangeState(next);
  }, []);

  return [range, setRange] as const;
};

/**
 * The wall clock, ticking.
 * ⚠️ Fiji, not the viewer's zone: a board read in Manila must show the hour the warehouse is
 * working in.
 */
export const useClock = (): string => {
  const [now, setNow] = useState(() => dayjs().tz(FIJI));
  useEffect(() => {
    const id = setInterval(() => setNow(dayjs().tz(FIJI)), 1000);
    return () => clearInterval(id);
  }, []);
  return now.format('HH:mm:ss');
};

/**
 * How many rows fit in a panel without the last one being sliced.
 *
 * 🚨 MEASURED, NOT ASSUMED. A fixed row count against a panel sized as a share of the screen
 * cuts rows in half at one resolution and leaves a gap at another. This reads the panel's own
 * height and the height of a row it actually rendered.
 *
 * Rows carry data-row. The TALLEST row seen since the last resize is the ruler, not the first:
 * one row higher than the one measured makes the estimate a row too generous, and the panel
 * clips that row instead of holding it back for the next page.
 *
 * 🚨 THE RULER ONLY EVER GROWS, and that is what makes this settle. A taller row can only be
 * found once drawn, so it re-measures after every render as well as on resize — safe, because a
 * growing ruler can only lower the count. A rule that could also raise it would oscillate.
 */
export const useFitRows = (
  ref: React.RefObject<HTMLElement | null>,
  revision: unknown,
): { perPage: number; measured: boolean } => {
  /**
   * 🚨 THE MEASUREMENT CARRIES ITS ROW SET, because a count belonging to the previous one is
   * worse than no count at all. Switching rider rendered the incoming card with the OUTGOING
   * card's fit: 7 stops read as a single page, so a rotation deciding whether every page had
   * been shown ended the turn on page one. The fitter corrected a frame later, so the card
   * painted "1/2" on a turn that had already been released.
   *
   * ⚠️ Until it has measured THIS set it reports 1, over-counting the pages. Over-counting
   * costs a wrong denominator for one frame before paint; under-counting loses a page for good.
   */
  const [fit, setFit] = useState<{ revision: unknown; perPage: number }>({ revision, perPage: 6 });
  const ruler = useRef(0);

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const rows = Array.from(el.querySelectorAll('[data-row]')) as HTMLElement[];
    const room = el.clientHeight;
    if (rows.length === 0 || room <= 0) return;
    const tallest = rows.reduce((max, row) => Math.max(max, row.offsetHeight), 0);
    if (tallest <= 0) return;
    ruler.current = Math.max(ruler.current, tallest);
    const fits = Math.max(1, Math.floor(room / ruler.current));
    setFit((was) =>
      was.perPage === fits && was.revision === revision ? was : { revision, perPage: fits },
    );
  };

  useLayoutEffect(measure);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // 🚨 FORGOTTEN WHENEVER THE ROWS CHANGE, not only on resize. The ruler only ever grows, so
    // one tall row — a wrapped address, a C&C row with its chip — pinned the count for the life
    // of the panel: a card with room for nine showed one, on every page after it.
    //
    // Monotonic WITHIN a page set, which is what stops it oscillating; relearnt across them.
    ruler.current = 0;
    measure();
    // A resize can change the rows as well as the room — a narrower card wraps what a wider one
    // did not — so the ruler is forgotten there too.
    const observer = new ResizeObserver(() => {
      ruler.current = 0;
      measure();
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, revision]);

  /**
   * 🚨 `measured` IS THE WHOLE POINT. A page count taken from the previous card's fit is worse
   * than none, because anything deciding "has this all been seen" would act on it. Saying so
   * lets the caller wait instead of guess; the fit itself still falls back to 1, which
   * over-counts pages and is the safe direction.
   */
  const measured = fit.revision === revision;
  return { perPage: measured ? fit.perPage : 1, measured };
};

/**
 * One clock for the whole board.
 *
 * 🚨 EVERY PANEL TURNS ITS PAGE ON THE SAME BEAT. Five cards on five timers looked like five
 * unrelated screens.
 *
 * 🚨 IT TICKS ONCE PER BEAT, NOT FIVE TIMES A SECOND. Publishing a percentage every 200ms
 * re-rendered the whole board to move one rectangle, minting a CSS class per frame and forcing
 * a layout each time. The bar is a CSS animation now (see MosaicPanel); this only says which
 * page is up.
 */
export const useBoardCycle = (seconds: number, enabled: boolean, offsetSeconds = 0) => {
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    setCycle(0);
    if (!enabled || seconds <= 0) return undefined;
    // ⚠️ OFFSET, NOT A DIFFERENT PERIOD (§1.1: "each turning on its own clock"). Five panels
    // started in one render all flip on the same frame, which reads as the whole screen
    // blinking. A stagger inside the beat keeps each panel readable while the next one turns.
    // It does NOT make a long panel quicker: ten pages at thirty seconds is five minutes
    // however it is clocked.
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = setTimeout(() => {
      interval = setInterval(() => setCycle((n) => n + 1), seconds * 1000);
    }, offsetSeconds * 1000);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [seconds, enabled, offsetSeconds]);

  return { cycle };
};

/**
 * Everything a panel needs to show whole rows and turn pages instead of scrolling.
 *
 * ⚠️ PAGING ONLY WHERE NOBODY CAN SCROLL. On the page every row renders and the browser does
 * what it always has; on the wall display the list is cut into pages that fit.
 */
export const usePagedRows = <T,>(
  rows: T[],
  cycle: number,
  enabled: boolean,
  pinned = 0,
  /**
   * ⚠️ WHOSE ROWS THESE ARE, for panels that swap one card for another. Keying the measurement
   * on the row count alone made two riders with the same number of stops look like no change at
   * all, so the second was drawn with the first's fit and counted as already measured.
   */
  key = '',
) => {
  const ref = useRef<HTMLDivElement | null>(null);
  /**
   * 🚨 ONE MEASUREMENT PER ROW SET, NOT PER PAGE. Re-measuring on each turn let perPage differ
   * between pages, while the slice offset `page * tailPerPage` is computed from the CURRENT
   * page's value — so pages overlapped and left gaps.
   *
   * ⚠️ The cost is a conservative count: the tallest row in the whole set sets the fit, so a
   * card can turn with space to spare. A row nobody ever sees is the worse failure.
   */
  const { perPage, measured } = useFitRows(ref, `${key}:${rows.length}`);

  /**
   * 🚨 PINNED ROWS SIT ON EVERY PAGE. Pinning to the top of a list that is then cut into pages
   * means "page one only" — a return, or a customer standing in a bay, disappeared for a
   * minute at a time from the rider's own card, which is the one place it had to be.
   */
  const head = rows.slice(0, pinned);
  const tail = rows.slice(head.length);
  const tailPerPage = Math.max(1, perPage - head.length);
  const pages = enabled ? Math.max(1, Math.ceil(tail.length / tailPerPage)) : 1;
  const page = pages <= 1 ? 0 : cycle % pages;
  const shown = enabled
    ? [...head, ...tail.slice(page * tailPerPage, (page + 1) * tailPerPage)]
    : rows;
  return { ref, shown, page, pages, measured };
};

/**
 * Steps a card through its riders, holding each for exactly as many beats as it has pages.
 *
 * 🚨 IT NEVER ASKS THE CARD A QUESTION. The version this replaces took a callback from the
 * pager saying "everything here has been seen", and that answer could arrive against the
 * previous rider's measurement — a seven-stop rider read as one page and gave up its turn on
 * page one. Here the only inputs are the beat and a page count the fitter has vouched for.
 *
 * ⚠️ EVERY UNCERTAIN STATE HOLDS. Not yet measured, a resize mid-turn, a rider list that
 * changed underneath — all of them keep the current rider on screen. A rider lingering a beat
 * too long is visible and harmless; a stop nobody ever sees is neither.
 *
 * ⚠️ maxBeats is the backstop, unchanged in meaning: a rider with more pages than that has its
 * tail cut, because the alternative is one tour holding a shared screen indefinitely.
 */
export const useRiderTurns = (
  riders: number,
  pages: number,
  measured: boolean,
  cycle: number,
  enabled: boolean,
  maxBeats: number,
) => {
  const [index, setIndex] = useState(0);
  const [startedAt, setStartedAt] = useState(cycle);
  const lastCycle = useRef(cycle);

  useEffect(() => {
    if (!enabled || riders <= 1) return;
    if (cycle === lastCycle.current) return;
    lastCycle.current = cycle;
    const elapsed = cycle - startedAt;
    const seenEveryPage = measured && elapsed >= pages;
    if (!seenEveryPage && elapsed < maxBeats) return;
    setIndex((i) => (i + 1) % riders);
    setStartedAt(cycle);
  }, [cycle, riders, pages, measured, enabled, maxBeats, startedAt]);

  // The board's cycle restarts at 0 when the beat is reconfigured; a turn that began on a
  // higher number would otherwise count backwards and stall on page one.
  useEffect(() => {
    if (cycle < startedAt) setStartedAt(cycle);
  }, [cycle, startedAt]);

  const safe = riders === 0 ? 0 : index % riders;
  return { index: safe, turnBeat: Math.max(0, cycle - startedAt) };
};

const TONE: Record<string, string> = {
  ok: 'success.main',
  warn: 'warning.main',
  bad: 'error.main',
  dim: 'text.secondary',
};

export const Pill = ({
  n,
  label,
  tone,
  title,
  solid = false,
}: {
  n: number | string;
  label: string;
  tone?: 'ok' | 'warn' | 'bad' | 'dim';
  /** Which set of orders this counts. Two populations on one strip need saying (DB-17). */
  title?: string;
  /** Filled rather than outlined, for the one count that means a person is waiting. */
  solid?: boolean;
}) => (
  <Chip
    variant={solid ? 'filled' : 'outlined'}
    title={title}
    sx={{
      opacity: tone === 'dim' ? 0.65 : 1,
      ...(solid ? { bgcolor: 'error.main', borderColor: 'error.main' } : {}),
    }}
    label={
      <>
        <Box
          component="span"
          sx={{ fontWeight: 700, color: solid ? '#fff' : (TONE[tone ?? ''] ?? 'text.primary') }}
        >
          {n}
        </Box>{' '}
        <Box component="span" sx={{ color: solid ? '#fff' : 'text.secondary' }}>
          {label}
        </Box>
      </>
    }
  />
);

export const PillSep = () => (
  <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider', mx: 0.5 }} />
);

/**
 * The board's own header.
 *
 * ⚠️ The feed line says what the board actually is. "LIVE" over a stale pull is the one caption
 * here that can mislead a passer-by into acting on old work, so the stale state changes the
 * words and the colour, not just a dot.
 */
export const Rail = ({
  tv,
  title = 'Dispatch board',
  feed,
  dayLabel,
  stale,
  asOfLabel,
  asOfFull,
  pills,
}: {
  tv: boolean;
  /** The board's own name, shown on the wall display only. */
  title?: string;
  /**
   * ⚠️ Which system the data came from — not always Locus. Unset keeps the Locus caption and
   * the tenant name with it.
   */
  feed?: string;
  dayLabel: string;
  stale: boolean;
  /** Short, for the stale tag: "11:58 FJT". */
  asOfLabel: string;
  /** Long, for the feed line: "Tue 20 Oct, 11:58 Fiji". */
  asOfFull: string;
  pills: React.ReactNode;
}) => {
  const clock = useClock();
  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 1, flex: '0 0 auto' }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
        {/* Only the wall display needs naming: on the page the app's own header already does. */}
        {tv && (
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {dayLabel}
        </Typography>
        {/* The time, not just the word: "stale" alone invites the reader to guess how stale. */}
        {stale && (
          <Chip
            variant="outlined"
            label={`Pulled ${asOfLabel} · not live`}
            sx={{ color: 'warning.main', borderColor: 'warning.main' }}
          />
        )}
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 0.75, ml: 'auto', minWidth: 0, justifyContent: 'flex-end' }}
        >
          {pills}
        </Stack>
      </Stack>

      {/* 🚨 A ROW OF ITS OWN. As the last children of a wrapping row, the feed caption and the
          clock fell wherever space was left — on a narrow rail, the far left under the date. */}
      <Stack direction="row" alignItems="center" sx={{ gap: 1, pt: 0.75 }}>
        <Typography
          variant="caption"
          sx={{
            whiteSpace: 'nowrap',
            color: stale ? 'warning.main' : 'text.secondary',
            fontWeight: stale ? 700 : 400,
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              mr: 0.75,
              bgcolor: stale ? 'warning.main' : 'success.main',
              animation: stale ? 'none' : 'dmbeat 2s ease-in-out infinite',
              '@keyframes dmbeat': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
          {feed ?? (TENANT ? `LOCUS PULL · ${TENANT}` : 'LOCUS PULL')}
          {' · '}
          <Box
            component="span"
            sx={{ color: stale ? 'text.secondary' : 'success.main', fontWeight: 700 }}
          >
            {asOfFull}
          </Box>
        </Typography>

        <Box sx={{ flex: 1 }} />

        <AccessTimeIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
        <Typography variant="h6" fontWeight={700} sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}>
          {clock}
        </Typography>
      </Stack>
    </Box>
  );
};

/**
 * Panel colours, read off the prototype's stylesheet rather than matched by eye.
 *
 * 🚨 THE TITLE IS NOT ALWAYS THE BAR, which is why there are two maps. `.bh h2` is `--ink-2`
 * for every panel and only three override it — the tour to `--rider`, Held back and the loading
 * queue to `--hold`. Waiting and Reassignments keep the default.
 */
const PANEL_ACCENT: Record<string, string> = {
  'b-idle': 'warning.main',
  'b-hero': 'primary.main',
  'b-wait': 'primary.main',
  'b-hold': 'warning.main',
  'b-moves': 'secondary.main',
  'c-here': 'error.main',
  'c-queue': 'primary.main',
};

const PANEL_TITLE: Record<string, string> = {
  'b-idle': 'warning.main',
  'b-hero': 'primary.main',
  'b-wait': 'text.secondary',
  'b-hold': 'warning.main',
  'b-moves': 'text.secondary',
  'c-here': 'error.main',
  'c-queue': 'text.secondary',
};

export const MosaicPanel = ({
  id,
  title,
  sub,
  meta,
  beat,
  cycle = 0,
  tv = false,
  children,
}: {
  id: string;
  title: string;
  sub?: string;
  /** The right-hand note: "13 orders out", "6 parked · page 1/5". */
  meta: React.ReactNode;
  /** Seconds per page turn. The bar's animation lasts exactly this long. */
  beat?: number;
  /**
   * 🚨 THE BAR IS RESTARTED BY THIS, not merely timed to match it. Giving the bar the same
   * duration as the pager left two clocks with one period and no shared start: the CSS
   * animation began at first paint, the pager's interval began when its effect ran, and they
   * drifted — the bar finished, then the rows changed some moments later. Keying the bar on the
   * cycle remounts it the instant the page turns, so it cannot be out of step by construction.
   */
  cycle?: number;
  tv?: boolean;
  children: React.ReactNode;
}) => {
  const accent = PANEL_ACCENT[id] ?? 'divider';
  const titleColour = PANEL_TITLE[id] ?? 'text.secondary';
  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        gridArea: id,
        minWidth: 0,
        minHeight: 0,
        // 🚨 Clipped on the wall, free on the page. The wall display is a fixed mosaic where a
        // row held back belongs on the next page; the page grows instead, and a card that clips
        // there simply cuts rows off at the bottom edge.
        overflow: tv ? 'hidden' : 'visible',
      }}
    >
      {/* The one piece of hand-written CSS that earns its place: a progress bar MUI has no
          component for.
          🚨 ANIMATED BY CSS, AND BY scaleX RATHER THAN width. Drawing it from React state cost a
          re-render and a new stylesheet rule per frame; scaleX runs on the compositor and costs
          no layout. The `key` is what keeps it honest — see the `cycle` prop. */}
      <Box
        key={cycle}
        sx={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 3,
          width: '100%',
          transformOrigin: 'left',
          bgcolor: accent,
          animation: beat ? `dmsweep ${beat}s linear infinite` : 'none',
          '@keyframes dmsweep': {
            from: { transform: 'scaleX(0)' },
            to: { transform: 'scaleX(1)' },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
      <Stack
        direction="row"
        alignItems="baseline"
        flexWrap="nowrap"
        sx={{ gap: 1, px: 2, pt: 1.5, pb: 1, flex: '0 0 auto' }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={800}
          letterSpacing={0.5}
          color={titleColour}
          sx={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
        >
          {title.toUpperCase()}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            {sub}
          </Typography>
        )}
        <Typography
          variant="subtitle2"
          fontWeight={800}
          color="text.secondary"
          sx={{ ml: 'auto', whiteSpace: 'nowrap', flex: '0 0 auto' }}
        >
          {meta}
        </Typography>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: 2,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          overflow: tv ? 'hidden' : 'visible',
        }}
      >
        {children}
      </Box>
    </Card>
  );
};

export const Empty = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
    {children}
  </Typography>
);

/**
 * 🚨 `flex: 1` ONLY ON THE WALL DISPLAY. On the page every rider renders at once, and a list
 * that claims the whole panel height makes the next rider's name land on top of the last one's
 * rows.
 */
export const Scroller = React.forwardRef<
  HTMLDivElement,
  { tv: boolean; children: React.ReactNode }
>(({ tv, children }, ref) => (
  <Box ref={ref} sx={{ flex: tv ? 1 : 'none', minHeight: 0, overflow: 'hidden' }}>
    {children}
  </Box>
));
Scroller.displayName = 'Scroller';

export const StopList = ({ children }: { children: React.ReactNode }) => <Box>{children}</Box>;

const KIND_COLOUR: Record<string, string> = {
  priority: 'warning.main',
  scheduled: 'secondary.main',
  parked: 'secondary.main',
  restock: 'warning.main',
  person: 'warning.main',
  // A customer parked in a bay with nobody bringing the order out is the most urgent row on
  // either board, so it takes the late colour rather than the waiting one.
  bay: 'error.main',
  gate: 'warning.main',
  // A return means the first delivery was wrong, so it outranks a normal drop (Nikhil, 23 Oct).
  rtn: 'error.main',
};

/** ⚠️ Only for codes that are not words. `cnc` is mapped before it exists in production. */
const KIND_LABEL: Record<string, string> = {
  rtn: 'Return',
  cnc: 'Click & Collect',
};

/**
 * ⚠️ RAW LOCUS CODES ARE NOT ENGLISH. Lower-casing gave "drop enroute", which is not a word,
 * and "pickup done", which on a DELIVERY board reads as the customer collecting when it means
 * the rider has the parcel. Anything absent still falls back to the prettified code.
 */
const VISIT_LABEL: Record<string, string> = {
  PICKUP_DONE: 'loaded',
  DROP_ENROUTE: 'on the way',
  DROP_ONGOING: 'delivering',
};

/**
 * The Locus delivery type.
 *
 * ⚠️ `open` renders NOTHING — it is the default, and a chip on every row is noise. An
 * unrecognised tier renders its own word, so a value Locus invents tomorrow is visible rather
 * than silently absorbed into Standard (DB-16).
 */
export const KindChip = ({ tier }: { tier: string }) => {
  if (!tier || tier === 'open') return null;
  const colour = KIND_COLOUR[tier];
  return (
    <Chip
      variant="outlined"
      label={KIND_LABEL[tier] ?? tier}
      sx={{ textTransform: 'capitalize', ...(colour && { color: colour, borderColor: colour }) }}
    />
  );
};

/**
 * The colour of a duration, which depends on WHICH WAY IT RUNS.
 *
 * ⚠️ TWO RULES, NOT ONE. A countdown is healthy while it is large — amber inside 20 minutes
 * (DB-22). A count-up is the opposite, and §5.8 puts it amber past 20 minutes and red past 45.
 * One rule for both painted an order that had waited an hour the same green as one assigned a
 * minute ago.
 */
export const ageColour = (age: number | null, countdown: boolean): string => {
  if (age === null) return 'text.secondary';
  if (countdown) return age <= 20 ? 'warning.main' : 'success.main';
  if (age > 45) return 'error.main';
  return age > 20 ? 'warning.main' : 'success.main';
};

const slotLabel = (row: AnalyticsRow): string => {
  const from = str(row, 'slot_start');
  const to = str(row, 'slot_end');
  if (!from || !to) return '';
  return `${dayjs(from).tz(FIJI).format('HH:mm')}–${dayjs(to).tz(FIJI).format('HH:mm')}`;
};

/**
 * One stop on a tour.
 *
 * ⚠️ NO STOP NUMBER. Locus exposes no route position on a task, and the prototype's numbering
 * was inferred from slot time and was wrong (DB-15).
 *
 * ⚠️ `age` IS CHOSEN BY THE PANEL. Assigned shows time since assignment and Active tour shows
 * time left in the slot — the same figure in both would mean two different things with nothing
 * on the row to tell them apart. `countdown` only controls the colour.
 */
export const StopRow = ({
  row,
  age,
  countdown = false,
  showState = true,
  tv = false,
  cnc,
}: {
  row: AnalyticsRow;
  age?: number | null;
  countdown?: boolean;
  /** Assigned orders are all ACCEPTED, so the chip says nothing there. */
  showState?: boolean;
  tv?: boolean;
  /**
   * 🚨 Set for EVERY Click & Collect order (§38). This reverses §33, where the treatment waited
   * for SuperStore's queue to agree with Locus: a live C&C order was then invisible until a
   * person assigned it by hand, which is the one stage it most needed the board. The bay and
   * stage still come from SuperStore where it knows them, and are derived where it does not.
   */
  cnc?: { bay: string; stage: string; waitingMin: number | null };
}) => {
  const overdue = num(row, 'is_overdue') === 1;
  const over = num(row, 'late_by_min');
  const visit = str(row, 'visit_state');
  // The stop the rider is working right now. Locus says so on the order itself, so this is
  // read rather than inferred — unlike the route position, which it never tells us.
  const current = visit === 'DROP_ENROUTE' || visit === 'DROP_ONGOING';
  return (
    <Stack
      data-row
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        py: 0.75,
        pl: cnc ? 1 : 0,
        borderBottom: 1,
        borderColor: 'divider',
        borderRadius: cnc ? 1 : 0,
        bgcolor: (t) =>
          cnc || overdue ? alpha(t.palette.error.main, cnc ? 0.14 : 0.07) : 'transparent',
        boxShadow: (t) => (cnc ? `inset 4px 0 0 ${t.palette.error.main}` : 'none'),
      }}
    >
      <Box
        component="span"
        sx={{
          width: '1em',
          textAlign: 'center',
          color: current ? 'primary.main' : 'text.disabled',
        }}
      >
        {current ? '▸' : '·'}
      </Box>
      {cnc ? (
        <Chip
          label="Click &amp; Collect"
          sx={{
            bgcolor: 'error.main',
            color: '#fff',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            height: 'auto',
            py: 0.4,
            '& .MuiChip-label': { fontSize: '1.05em' },
          }}
        />
      ) : (
        <KindChip tier={str(row, 'sla_tier')} />
      )}
      {/* ⚠️ A BADGE, NOT TEXT. The bay is what a dispatcher shouts across a warehouse, so it
          reads as a label rather than part of a sentence — and it replaces the address, which
          for a C&C order IS the collection point and says nothing twice. */}
      {cnc && (
        <Box
          component="span"
          sx={{
            bgcolor: '#fff',
            color: '#E5484D',
            fontWeight: 800,
            borderRadius: '6px',
            px: 1.25,
            py: 0.25,
            fontSize: '1.05em',
            letterSpacing: 0.5,
            whiteSpace: 'nowrap',
          }}
        >
          {cnc.bay}
        </Box>
      )}
      <Typography
        variant="body2"
        fontWeight={700}
        color="primary.main"
        sx={{ whiteSpace: 'nowrap' }}
      >
        {str(row, 'order_id')}
      </Typography>
      {/* 🚨 WRAPPED ON THE WALL, ELLIPSISED ON THE PAGE (DB-19). Nobody standing in front of a
          television can hover a truncated address, so there the row takes a second line. On the
          page the column is narrow and a reader has a mouse, so it ellipsises and the full text
          is the tooltip — wrapping there broke "Pasifika Campus" into one letter per line. */}
      {/* ⚠️ The bay replaces the address on a waiting C&C row, because the address IS the
          collection point and saying it twice tells a dispatcher nothing. */}
      <Typography
        variant="body2"
        noWrap={!tv}
        title={cnc ? `${cnc.bay}` : str(row, 'address_text')}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          fontWeight: cnc || current ? 700 : 400,
          color: cnc ? 'error.main' : 'inherit',
        }}
      >
        {(cnc && cnc.stage) || str(row, 'address_text') || '—'}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}
      >
        {slotLabel(row)}
      </Typography>
      {showState && visit && (
        <Chip
          variant="outlined"
          label={VISIT_LABEL[visit] ?? visit.replace(/_/g, ' ').toLowerCase()}
        />
      )}
      {/* ⚠️ Stood down once the slot has passed: slot_remaining_min counts DOWN, so past it the
          value is negative and prints "-1d 3h" beside a red "1d 3h over". */}
      {overdue && (
        <Chip
          color="error"
          label={over === null ? 'LATE' : `${formatMinutes(over)} over`}
          sx={{ fontWeight: 700 }}
        />
      )}
      {age !== undefined && !overdue && (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            minWidth: '4em',
            textAlign: 'right',
            color: cnc ? 'error.main' : ageColour(age, countdown),
            ...(cnc && { minWidth: '6em' }),
          }}
        >
          {age === null ? '—' : formatMinutes(age)}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * One rider's card.
 *
 * 🚨 `summary` is decided by the caller. stop_count and dropped_count are NULL until the tour
 * itself is ingested, and null there means "not known", never zero — rendering "0 of 8 dropped"
 * on a wall reads as a rider who has delivered nothing all afternoon.
 */
export const HeroTour = ({
  rider,
  summary,
  farNote,
  leftLabel,
  rightLabel,
  roster,
  current,
  alerts = [],
  urgent = [],
  children,
}: {
  rider: string;
  summary: React.ReactNode;
  /** The far-right figure on the rider line, e.g. the longest wait on this card. */
  farNote?: React.ReactNode;
  /**
   * ⚠️ "by slot" is the most this may ever say. Locus exposes no route position (DB-15), so the
   * board does not know what order a rider will drop in. The rows ARE sorted by slot.
   */
  leftLabel?: string;
  rightLabel?: string;
  roster: string[];
  current: string;
  /**
   * 🚨 Riders whose card holds something urgent. At thirty seconds a page, a rider's tab can be
   * off screen for over a minute, so an overdue stop would otherwise be invisible exactly while
   * somebody else's tour is showing. These get the dot.
   */
  alerts?: string[];
  /**
   * ⚠️ THE RED NAME IS NARROWER THAN THE DOT, and deliberately so. Colouring the name for every
   * overdue stop turned most tabs red on a busy evening — 7 overdue across 4 riders, 30 Oct —
   * and a signal every tab carries is not a signal. Only Click & Collect earns the colour.
   */
  urgent?: string[];
  children: React.ReactNode;
}) => (
  <>
    <Stack
      direction="row"
      alignItems="baseline"
      flexWrap="wrap"
      useFlexGap
      sx={{ gap: 1.5, pb: 1 }}
    >
      <Typography variant="subtitle1" fontWeight={700}>
        {rider}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ '& b': { color: 'success.main' } }}
      >
        {summary}
      </Typography>
      {farNote && (
        <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
          {farNote}
        </Typography>
      )}
    </Stack>

    {(leftLabel || rightLabel) && (
      <Stack direction="row" alignItems="baseline" sx={{ pb: 0.5, flex: '0 0 auto' }}>
        {leftLabel && (
          <Typography variant="caption" color="text.disabled" textTransform="uppercase">
            {leftLabel}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {rightLabel && (
          <Typography variant="caption" color="text.disabled" textTransform="uppercase">
            {rightLabel}
          </Typography>
        )}
      </Stack>
    )}

    {children}

    {roster.length > 1 && (
      <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.5, pt: 1, flex: '0 0 auto' }}>
        {roster.map((name) => (
          <Box key={name} sx={{ position: 'relative', display: 'inline-flex' }}>
            {/* ⚠️ THE NAME TURNS RED, NOT JUST THE DOT (§38). A dot is a mark beside a word;
                the colour is what carries across a room, and the tab is how a reader learns
                which rider holds the thing they must not miss while someone else is on screen. */}
            <Chip
              label={name}
              variant={name === current ? 'filled' : 'outlined'}
              sx={{
                ...(name === current && {
                  bgcolor: 'primary.main',
                  color: 'background.paper',
                  fontWeight: 700,
                }),
                ...(urgent.includes(name) && {
                  fontWeight: 700,
                  ...(name === current
                    ? { bgcolor: 'error.main', color: '#fff' }
                    : { color: 'error.main', borderColor: 'error.main' }),
                }),
              }}
            />
            {alerts.includes(name) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  border: 2,
                  borderColor: 'background.paper',
                }}
              />
            )}
          </Box>
        ))}
      </Stack>
    )}
  </>
);

/** Waiting, Held back and Reassignments all read the same way: tag, id, what, how long. */
export const PlainRow = ({
  kind,
  tag,
  cnc,
  id,
  what,
  muted,
  ago,
}: {
  /** Drives both the chip colour and the row's left stripe. */
  kind?: 'parked' | 'restock' | 'cancelled' | 'person' | 'auto' | 'bay' | 'gate';
  tag?: string;
  /**
   * 🚨 Set for every Click & Collect order (§38), in whichever panel holds it. It replaces the
   * tag, the text and the stripe together: a C&C row is not a normal row wearing a chip.
   */
  cnc?: { bay: string; stage: string };
  id: string;
  what: string;
  /**
   * 🚨 `what` IS TWO DIFFERENT FACTS IN ONE COLUMN. Held back prints the parking reason where
   * Locus recorded one and the delivery address where it did not — 122 of 199 parked orders,
   * measured 21 Oct 2026. Dimmed means the address is standing in for a reason; the tooltip
   * says so in words.
   */
  muted?: boolean;
  ago: number | null;
}) => {
  const colour = kind ? KIND_COLOUR[kind] : undefined;
  return (
    <Stack
      data-row
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        py: 0.75,
        pl: 1,
        borderBottom: 1,
        borderColor: 'divider',
        // The stripe carries the state; the row background is left alone.
        ...(colour && { borderLeft: 3, borderLeftColor: colour }),
        // ⚠️ A C&C row overrides whatever state it is in. A customer standing at a collection
        // point outranks the reason an order is parked or the fact that it has no rider.
        ...(cnc && {
          borderLeft: 4,
          borderLeftColor: 'error.main',
          borderRadius: 1,
          bgcolor: (t: Theme) => alpha(t.palette.error.main, 0.14),
        }),
      }}
    >
      {cnc && (
        <>
          <Chip
            label="Click &amp; Collect"
            sx={{
              bgcolor: 'error.main',
              color: '#fff',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              height: 'auto',
              py: 0.4,
              '& .MuiChip-label': { fontSize: '1.05em' },
            }}
          />
          <Box
            component="span"
            sx={{
              bgcolor: '#fff',
              color: '#E5484D',
              fontWeight: 800,
              borderRadius: '6px',
              px: 1.25,
              py: 0.25,
              fontSize: '1.05em',
              letterSpacing: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            {cnc.bay}
          </Box>
        </>
      )}
      {!cnc && tag && (
        <Chip
          variant="outlined"
          label={tag}
          sx={{ textTransform: 'uppercase', ...(colour && { color: colour, borderColor: colour }) }}
        />
      )}
      <Typography
        variant="body2"
        fontWeight={700}
        color="primary.main"
        sx={{ whiteSpace: 'nowrap' }}
      >
        {id}
      </Typography>
      <Typography
        variant="body2"
        noWrap
        title={muted ? `No reason recorded — showing the delivery address: ${what}` : what}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          ...(muted && !cnc && { color: 'text.secondary' }),
          ...(cnc?.stage && { fontWeight: 700 }),
        }}
      >
        {/* ⚠️ A C&C row with no stage and no real reason says nothing here — `muted` means the
            address is standing in for a reason, and the bay badge beside it already is that
            address. Repeating it is noise on the one row that must read instantly. */}
        {cnc ? cnc.stage || (muted ? '' : what) : what || '—'}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={700}
        color="text.secondary"
        sx={{ whiteSpace: 'nowrap' }}
      >
        {ago === null ? '—' : `${formatMinutes(ago)} ago`}
      </Typography>
    </Stack>
  );
};

/**
 * The red band under the rail: who is standing at the collection point right now.
 *
 * 🚨 IT NEVER ROTATES AWAY (Nikhil, 23 Oct). At thirty seconds a page a rider's tab can be off
 * screen for over a minute, so a customer waiting in a bay would vanish from the board for
 * longer than they have been waiting. The strip is outside the rotation for that reason.
 *
 * ⚠️ It renders nothing at all when nobody is waiting — not an empty bar, not a "0 waiting"
 * caption. A permanent red band on a wall teaches people to stop seeing red.
 */
export type CncStripRow = {
  order: string;
  bay: string;
  stage: string;
  waitingMin: number | null;
  flashing: boolean;
  customerHere: boolean;
};

/**
 * The red band under the rail: every Click & Collect order nobody can act on yet.
 *
 * 🚨 IT NEVER ROTATES AWAY (§33, §38). At thirty seconds a page a rider's tab is off screen for
 * over a minute, so a customer standing at a collection point would vanish from the board for
 * longer than they had been waiting. The strip sits outside the rotation for that reason.
 *
 * ⚠️ IT RENDERS NOTHING when there is nothing to act on — not an empty bar, not a "0 waiting"
 * caption. A permanent red band on a wall teaches people to stop seeing red.
 *
 * ⚠️ CAPPED AT max LINES (Nikhil, 30 Oct). One line per order is right for one or two; ten
 * would eat the space the five panels need. The longest-waiting are kept and the rest counted.
 */
export const CncStrip = ({ rows, max }: { rows: CncStripRow[]; max: number }) => {
  if (rows.length === 0) return null;
  const shown = rows.slice(0, max);
  const hidden = rows.length - shown.length;
  return (
    <Box
      sx={{
        flex: '0 0 auto',
        mx: 2,
        mb: 1.5,
        px: '14px',
        py: '9px',
        borderRadius: '6px',
        bgcolor: '#E5484D',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,

        // A single keyframe on the container, so a flashing row cannot re-measure the panels
        // underneath it the way a React-driven blink would.
        '@keyframes cncFlash': { '50%': { opacity: 0.45 } },
      }}
    >
      {shown.map((row) => (
        <Stack
          key={row.order}
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ whiteSpace: 'nowrap' }}
        >
          <Box
            component="span"
            sx={{
              bgcolor: '#fff',
              color: '#E5484D',
              fontWeight: 800,
              borderRadius: '6px',
              px: 1.25,
              py: 0.25,
              fontSize: '1.15em',
              letterSpacing: 0.5,
            }}
          >
            {row.bay}
          </Box>
          <Typography variant="subtitle1" fontWeight={800} letterSpacing={0.5}>
            CLICK &amp; COLLECT · {row.order}
          </Typography>
          <Box
            component="span"
            sx={{
              border: '1px solid rgba(255,255,255,0.7)',
              borderRadius: '4px',
              px: 1,
              py: 0.1,
              fontWeight: 700,
              fontSize: '0.85em',
              letterSpacing: 0.5,
              ...(row.flashing && { animation: 'cncFlash 1.2s ease-in-out infinite' }),
            }}
          >
            {row.stage}
          </Box>
          <Box sx={{ flex: 1 }} />
          {row.waitingMin !== null && (
            <Typography variant="subtitle1" fontWeight={800}>
              {row.customerHere
                ? `customer waiting ${formatMinutes(row.waitingMin)}`
                : `ordered ${formatMinutes(row.waitingMin)} ago`}
            </Typography>
          )}
        </Stack>
      ))}
      {hidden > 0 && (
        <Typography variant="body2" fontWeight={700} sx={{ opacity: 0.85 }}>
          +{hidden} more
        </Typography>
      )}
    </Box>
  );
};

/**
 * The takeover shown when the pull has gone cold.
 *
 * It states the time rather than only the fact, and says the board recovers on its own so
 * nobody goes looking for the television's remote.
 */
export const StaleCage = ({ asOfLabel }: { asOfLabel: string }) => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      zIndex: 5,
      textAlign: 'center',
      p: 3,
    }}
  >
    <Typography variant="h3" fontWeight={800} color="warning.main" textTransform="uppercase">
      Data is stale
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '40em' }}>
      Last update {asOfLabel}. The board is not live. It reconnects on its own — nobody needs to
      touch the screen.
    </Typography>
  </Box>
);
